import { DateTime } from "luxon";
import { Typography, Box, Chip, Tooltip } from "@mui/material";
import { GridActionsCellItem } from "@mui/x-data-grid";
import {
  Visibility,
  CheckCircle,
  Schedule,
  Download,
} from "@mui/icons-material";

const upcomingNotarizationActions = (
  extraSmall,
  row,
  handleUpcomingNotarizationClick,
) => {
  const bookedDateUtc = DateTime.fromISO(row?.booked_date, {
    zone: "utc",
  });
  const nowUTC = DateTime.utc();
  const timeDifference = bookedDateUtc - nowUTC;

  let status = "Scheduled";
  let color = "primary";

  if (timeDifference <= 300000) {
    status = "Ready to Start";
    color = "success";
  } else if (timeDifference <= 3600000) {
    // 1 hour
    status = "Starting Soon";
    color = "warning";
  }

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: extraSmall ? "row" : "column",
          alignItems: extraSmall ? "center" : "flex-end",
          justifyContent: extraSmall ? "space-between" : undefined,
          pb: extraSmall ? 0 : 0.2,
          pr: extraSmall ? 0 : 1,
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: 0.5,
            order: extraSmall ? 2 : 1,
          }}
        >
          <GridActionsCellItem
            icon={
              <Tooltip title="View Details">
                <Visibility />
              </Tooltip>
            }
            label="View"
            onClick={() => handleUpcomingNotarizationClick(row)}
          />
        </Box>
        <Chip
          label={status}
          color={color}
          size="small"
          sx={{
            height: "20px",
            fontSize: "12px",
            width: "max-content",
            marginTop: extraSmall ? 0 : "2px",
            order: extraSmall ? 1 : 2,
          }}
        />
      </Box>
    </>
  );
};

export const upcomingNotarizationColumns = (
  extraSmall,
  tabletSize,
  currentUser,
  handleUpcomingNotarizationClick,
) =>
  tabletSize
    ? [
        {
          field: "booked_date",
          headerName: "Upcoming Appointments",
          flex: 1,
          editable: false,
          renderCell: (params) => {
            const utcDate = DateTime.fromISO(params?.value, { zone: "utc" });
            const usDate = utcDate.setZone(currentUser?.timezone);
            const formattedDate = usDate.toFormat("MMMM dd, yyyy hh:mm a");

            return (
              <>
                <Box sx={{ display: "flex", width: "100%", pr: 2 }}>
                  <Box
                    sx={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      // borderBottom: "1px solid rgba(0, 0, 0, 0.1)",
                      pb: 0.2,
                      pl: 1,
                      minWidth: "180px",
                      width: "100%",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "0.90rem",
                        color: "rgba(0, 0, 0, 0.8)",
                        fontWeight: 500,
                      }}
                    >
                      {formattedDate}
                    </Typography>

                    <Typography
                      variant="body1"
                      sx={{
                        fontSize: "0.85rem",
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                        color: "rgba(0,0,0,0.62)",
                      }}
                    >
                      {params?.row?.signer?.fullName}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontSize: "0.85rem",
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                        color: "rgba(0,0,0,0.62)",
                      }}
                    >
                      {params?.row?.signer?.email}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontSize: "0.85rem",
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                        color: "rgba(0,0,0,0.62)",
                      }}
                    >
                      {params?.row?.project_address}
                    </Typography>
                    {extraSmall &&
                      upcomingNotarizationActions(
                        extraSmall,
                        params?.row,
                        handleUpcomingNotarizationClick,
                      )}
                  </Box>
                  {!extraSmall &&
                    upcomingNotarizationActions(
                      extraSmall,
                      params?.row,
                      handleUpcomingNotarizationClick,
                    )}
                </Box>
              </>
            );
          },
        },
      ]
    : [
        {
          field: "booked_date",
          headerName: "Appointment Time",
          flex: 1,
          renderCell: (params) => {
            const utcDate = DateTime.fromISO(params.value, { zone: "utc" });
            const usDate = utcDate.setZone(currentUser?.timezone);
            const formattedDate = usDate.toFormat("MMMM dd, yyyy hh:mm a");
            return <span>{formattedDate}</span>;
          },
        },
        {
          field: "signer",
          headerName: "Signer",
          flex: 0.8,
          renderCell: (params) => (
            <Box>
              <Typography variant="body2" fontWeight={500}>
                {params.row.signer?.fullName}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {params.row.signer?.email}
              </Typography>
            </Box>
          ),
        },
        {
          field: "project_address",
          headerName: "Project Address",
          flex: 1,
        },
        {
          field: "status",
          headerName: "Status",
          flex: 0.6,
          renderCell: (params) => {
            const bookedDateUtc = DateTime.fromISO(params.row.booked_date, {
              zone: "utc",
            });
            const nowUTC = DateTime.utc();
            const timeDifference = bookedDateUtc - nowUTC;

            let status = "Scheduled";
            let color = "primary";

            if (timeDifference <= 300000) {
              status = "Ready to Start";
              color = "success";
            } else if (timeDifference <= 3600000) {
              // 1 hour
              status = "Starting Soon";
              color = "warning";
            }

            return <Chip label={status} color={color} size="small" />;
          },
        },
        {
          field: "actions",
          type: "actions",
          headerName: "Actions",
          width: 100,
          getActions: (params) => [
            <GridActionsCellItem
              icon={
                <Tooltip title="View Details">
                  <Visibility />
                </Tooltip>
              }
              label="View"
              onClick={() => handleUpcomingNotarizationClick(params.row)}
            />,
          ],
        },
      ];

const pastNotarizationActions = (
  extraSmall,
  row,
  isCompleted,
  handlePastNotarizationClick,
  handleDocumentDownload,
) => (
  <>
    <Box
      sx={{
        display: "flex",
        flexDirection: extraSmall ? "row" : "column",
        alignItems: extraSmall ? "center" : "flex-end",
        justifyContent: extraSmall ? "space-between" : undefined,
        pb: extraSmall ? 0 : 0.2,
        pr: extraSmall ? 0 : 1,
      }}
    >
      <Box
        sx={{
          display: "flex",
          gap: 0.5,
          order: extraSmall ? 2 : 1,
        }}
      >
        <GridActionsCellItem
          icon={
            <Tooltip title="View Details">
              <Visibility />
            </Tooltip>
          }
          label="View"
          onClick={() => handlePastNotarizationClick(row)}
        />
        {row?.is_notarized && (
          <GridActionsCellItem
            icon={
              <Tooltip title="Download Documents">
                <Download />
              </Tooltip>
            }
            label="Download"
            onClick={() => handleDocumentDownload(row)}
          />
        )}
      </Box>
      <Chip
        label={isCompleted ? "Completed" : "Cancelled"}
        color={isCompleted ? "success" : "error"}
        size="small"
        sx={{
          height: "20px",
          fontSize: "12px",
          width: "max-content",
          marginTop: extraSmall ? 0 : "2px",
          order: extraSmall ? 1 : 2,
        }}
      />
    </Box>
  </>
);

export const pastNotarizationColumns = (
  extraSmall,
  tabletSize,
  currentUser,
  handlePastNotarizationClick,
  handleDocumentDownload,
) =>
  tabletSize
    ? [
        {
          field: "completed_date",
          headerName: "Past Notarization History",
          flex: 1,
          editable: false,
          renderCell: (params) => {
            const completedDocs = params?.row?.completed_documents || [];
            let completionTimestamp = params?.row?.completed_at || null;

            if (!completionTimestamp && completedDocs.length > 0) {
              // Find the latest document creation timestamp
              const latestDoc = completedDocs.reduce((latest, doc) => {
                return !latest || doc.created_at > latest.created_at
                  ? doc
                  : latest;
              }, null);
              completionTimestamp = latestDoc?.created_at;
            }

            if (!completionTimestamp && params?.row?.cancelled_at) {
              completionTimestamp = params?.row?.cancelled_at;
            }

            if (!completionTimestamp) {
              return <span>N/A</span>;
            }

            // Convert Unix timestamp to DateTime
            const utcDate = DateTime.fromSeconds(completionTimestamp);
            const usDate = utcDate.setZone(currentUser?.timezone);
            const formattedDate = usDate.toFormat("MMMM dd, yyyy hh:mm a");

            const isCompleted = params?.row?.is_notarized;

            return (
              <>
                <Box sx={{ display: "flex", width: "100%", pr: 2 }}>
                  <Box
                    sx={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      // borderBottom: "1px solid rgba(0, 0, 0, 0.1)",
                      pb: 0.2,
                      pl: 1,
                      minWidth: "180px",
                      width: "100%",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "0.90rem",
                        color: "rgba(0, 0, 0, 0.8)",
                        fontWeight: 500,
                      }}
                    >
                      {formattedDate}
                    </Typography>

                    <Typography
                      variant="body1"
                      sx={{
                        fontSize: "0.85rem",
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                        color: "rgba(0,0,0,0.62)",
                      }}
                    >
                      {params?.row?.signer_details?.first}{" "}
                      {params?.row?.signer_details?.middle}{" "}
                      {params?.row?.signer_details?.last}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontSize: "0.85rem",
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                        color: "rgba(0,0,0,0.62)",
                      }}
                    >
                      {params?.row?.signer_details?.email}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontSize: "0.85rem",
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                        color: "rgba(0,0,0,0.62)",
                      }}
                    >
                      {params?.row?.project_address}
                    </Typography>
                    {extraSmall &&
                      pastNotarizationActions(
                        extraSmall,
                        params?.row,
                        isCompleted,
                        handlePastNotarizationClick,
                        handleDocumentDownload,
                      )}
                  </Box>
                  {!extraSmall &&
                    pastNotarizationActions(
                      extraSmall,
                      params?.row,
                      isCompleted,
                      handlePastNotarizationClick,
                      handleDocumentDownload,
                    )}
                </Box>
              </>
            );
          },
        },
      ]
    : [
        {
          field: "completed_date",
          headerName: "Completion Date",
          flex: 1,
          renderCell: (params) => {
            // Prefer signer-level completion timestamp, then documents, then cancelled timestamp.
            const completedDocs = params.row.completed_documents || [];
            let completionTimestamp = params.row.completed_at || null;

            if (!completionTimestamp && completedDocs.length > 0) {
              // Find the latest document creation timestamp
              const latestDoc = completedDocs.reduce((latest, doc) => {
                return !latest || doc.created_at > latest.created_at
                  ? doc
                  : latest;
              }, null);
              completionTimestamp = latestDoc?.created_at;
            }

            if (!completionTimestamp && params.row.cancelled_at) {
              completionTimestamp = params.row.cancelled_at;
            }

            if (!completionTimestamp) {
              return <span>N/A</span>;
            }

            // Convert Unix timestamp to DateTime
            const utcDate = DateTime.fromSeconds(completionTimestamp);
            const usDate = utcDate.setZone(currentUser?.timezone);
            const formattedDate = usDate.toFormat("MMMM dd, yyyy hh:mm a");
            return <span>{formattedDate}</span>;
          },
        },
        {
          field: "signer",
          headerName: "Signer",
          flex: 0.8,
          renderCell: (params) => (
            <Box display={"flex"} flexDirection={"column"}>
              <Typography variant="body2" fontWeight={500}>
                {params.row.signer_details?.first}{" "}
                {params.row.signer_details?.middle}{" "}
                {params.row.signer_details?.last}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {params.row.signer_details?.email}
              </Typography>
            </Box>
          ),
        },
        {
          field: "project_address",
          headerName: "Project Address",
          flex: 1,
        },
        {
          field: "status",
          headerName: "Status",
          flex: 0.6,
          renderCell: (params) => {
            const isCompleted = params.row.is_notarized;
            return (
              <Chip
                label={isCompleted ? "Completed" : "Cancelled"}
                color={isCompleted ? "success" : "error"}
                size="small"
              />
            );
          },
        },
        {
          field: "actions",
          type: "actions",
          headerName: "Actions",
          width: 140,
          getActions: (params) => {
            const actions = [
              <GridActionsCellItem
                icon={
                  <Tooltip title="View Details">
                    <Visibility />
                  </Tooltip>
                }
                label="View"
                onClick={() => handlePastNotarizationClick(params.row)}
              />,
              <GridActionsCellItem
                icon={
                  <Tooltip title="Download Documents">
                    <Download />
                  </Tooltip>
                }
                label="Download"
                onClick={() => handleDocumentDownload(params.row)}
                sx={{
                  visibility: params.row?.is_notarized ? "visible" : "hidden",
                }}
              />,
            ];

            return actions;
          },
        },
      ];

const realTimeRequestActions = (
  row,
  handleRealTimeRequestClick,
  handleAcceptRealTimeRequest,
) => (
  <Box
    sx={{
      display: "flex",
      gap: 0.5,
      alignItems: "flex-start",
    }}
  >
    <GridActionsCellItem
      icon={
        <Tooltip title="View Details">
          <Visibility />
        </Tooltip>
      }
      label="View"
      onClick={() => handleRealTimeRequestClick(row)}
    />
    <GridActionsCellItem
      icon={
        <Tooltip title="Accept Request">
          <CheckCircle />
        </Tooltip>
      }
      label="Accept"
      onClick={() => handleAcceptRealTimeRequest(row?.id)}
    />
  </Box>
);

export const realTimeRequestColumns = (
  extraSmall,
  tabletSize,
  currentUser,
  currentTime,
  getRequestExpiryTime,
  handleRealTimeRequestClick,
  handleAcceptRealTimeRequest,
) =>
  tabletSize
    ? [
        {
          field: "created_at",
          headerName: "Real-Time Requests",
          flex: 1,
          editable: false,
          renderCell: (params) => {
            // Reference currentTime to force re-render every second
            // eslint-disable-next-line no-unused-vars
            const _ = currentTime;

            const expiryTime = getRequestExpiryTime(params?.row);

            if (!expiryTime || !expiryTime.isValid) {
              return <Chip label="N/A" color="default" size="small" />;
            }

            const now = DateTime.utc();
            const timeLeft = expiryTime.diff(now);

            const minutesLeft = Math.floor(timeLeft.as("minutes"));
            const secondsLeft = Math.floor(timeLeft.as("seconds") % 60);

            const utcDate = DateTime.fromISO(params?.value, { zone: "utc" });
            const usDate = utcDate.setZone(currentUser?.timezone);
            const formattedDate = usDate.toFormat("MMMM dd, yyyy hh:mm a");

            return (
              <>
                <Box sx={{ display: "flex", width: "100%", pr: 2 }}>
                  <Box
                    sx={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      // borderBottom: "1px solid rgba(0, 0, 0, 0.1)",
                      pb: 0.2,
                      pl: 1,
                      minWidth: "180px",
                      width: "100%",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "0.90rem",
                        color: "rgba(0, 0, 0, 0.8)",
                        fontWeight: 500,
                      }}
                    >
                      {formattedDate}
                    </Typography>

                    <Typography
                      variant="body1"
                      sx={{
                        fontSize: "0.85rem",
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                        color: "rgba(0,0,0,0.62)",
                      }}
                    >
                      {params?.row?.requester?.fullName ||
                        params?.row?.requestor ||
                        "N/A"}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontSize: "0.85rem",
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                        color: "rgba(0,0,0,0.62)",
                      }}
                    >
                      {params?.row?.requester?.email || "N/A"}
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                          alignItems: "center",
                          mt: 0.5,
                        }}
                      >
                        <Chip
                          label={
                            params?.row?.type === "immediate"
                              ? "Expedited"
                              : "Later Date"
                          }
                          color={
                            params?.row?.type === "immediate"
                              ? "error"
                              : "warning"
                          }
                          size="small"
                        />
                        {timeLeft.milliseconds <= 0 ? (
                          <Chip label="Expired" color="error" size="small" />
                        ) : (
                          <Chip
                            label={`${minutesLeft}:${secondsLeft.toString().padStart(2, "0")}`}
                            color="warning"
                            size="small"
                          />
                        )}
                      </Box>

                      {extraSmall &&
                        realTimeRequestActions(
                          params?.row,
                          handleRealTimeRequestClick,
                          handleAcceptRealTimeRequest,
                        )}
                    </Box>
                  </Box>
                  {!extraSmall &&
                    realTimeRequestActions(
                      params?.row,
                      handleRealTimeRequestClick,
                      handleAcceptRealTimeRequest,
                    )}
                </Box>
              </>
            );
          },
        },
      ]
    : [
        {
          field: "created_at",
          headerName: "Request Time",
          flex: 1,
          renderCell: (params) => {
            const utcDate = DateTime.fromISO(params.value, { zone: "utc" });
            const usDate = utcDate.setZone(currentUser?.timezone);
            const formattedDate = usDate.toFormat("MMMM dd, yyyy hh:mm a");
            return (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  height: "100%",
                  width: "100%",
                }}
              >
                <Typography variant="body2" fontWeight={500} noWrap={false}>
                  {formattedDate}
                </Typography>
              </Box>
            );
          },
        },
        {
          field: "requester",
          headerName: "Requester",
          flex: 2,
          minWidth: 300,
          renderCell: (params) => (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                height: "100%",
                width: "100%",
              }}
            >
              <Typography variant="body2" fontWeight={500} noWrap={false}>
                {params.row.requester?.fullName ||
                  params.row.requestor ||
                  "N/A"}
              </Typography>
              <Typography
                variant="caption"
                color="textSecondary"
                noWrap={false}
              >
                {params.row.requester?.email || "N/A"}
              </Typography>
            </Box>
          ),
        },
        {
          field: "type",
          headerName: "Type",
          flex: 0.6,
          renderCell: (params) => (
            <Chip
              label={params.value === "immediate" ? "Expedited" : "Later Date"}
              color={params.value === "immediate" ? "error" : "warning"}
              size="small"
            />
          ),
        },
        {
          field: "time_remaining",
          headerName: "Time Left",
          flex: 0.6,
          renderCell: (params) => {
            // Reference currentTime to force re-render every second
            // eslint-disable-next-line no-unused-vars
            const _ = currentTime;

            const expiryTime = getRequestExpiryTime(params.row);

            if (!expiryTime || !expiryTime.isValid) {
              return <Chip label="N/A" color="default" size="small" />;
            }

            const now = DateTime.utc();
            const timeLeft = expiryTime.diff(now);

            if (timeLeft.milliseconds <= 0) {
              return <Chip label="Expired" color="error" size="small" />;
            }

            const minutesLeft = Math.floor(timeLeft.as("minutes"));
            const secondsLeft = Math.floor(timeLeft.as("seconds") % 60);

            return (
              <Chip
                label={`${minutesLeft}:${secondsLeft.toString().padStart(2, "0")}`}
                color="warning"
                size="small"
              />
            );
          },
        },
        {
          field: "actions",
          type: "actions",
          headerName: "Actions",
          width: 140,
          getActions: (params) => [
            <GridActionsCellItem
              icon={
                <Tooltip title="View Details">
                  <Visibility />
                </Tooltip>
              }
              label="View"
              onClick={() => handleRealTimeRequestClick(params.row)}
            />,
            <GridActionsCellItem
              icon={
                <Tooltip title="Accept Request">
                  <CheckCircle />
                </Tooltip>
              }
              label="Accept"
              onClick={() => handleAcceptRealTimeRequest(params.row.id)}
            />,
          ],
        },
      ];

const expiredRequestActions = (
  row,
  handleExpiredExpeditedRequestClick,
  openExpiredExpeditedAcceptConfirm,
) => (
  <Box
    sx={{
      display: "flex",
      gap: 0.5,
      alignItems: "flex-start",
    }}
  >
    <GridActionsCellItem
      icon={
        <Tooltip title="View Details">
          <Visibility />
        </Tooltip>
      }
      label="View"
      onClick={() => handleExpiredExpeditedRequestClick(row)}
    />
    <GridActionsCellItem
      icon={
        <Tooltip title="Accept Request">
          <Schedule />
        </Tooltip>
      }
      label="Accept"
      onClick={() => openExpiredExpeditedAcceptConfirm(row)}
    />
  </Box>
);

export const expiredRequestColumns = (
  extraSmall,
  tabletSize,
  currentUser,
  currentTime,
  handleExpiredExpeditedRequestClick,
  openExpiredExpeditedAcceptConfirm,
) =>
  tabletSize
    ? [
        {
          field: "requested_created_at",
          headerName: "Expired Request",
          flex: 1,
          editable: false,
          renderCell: (params) => {
            const rawValue = params.value;
            const utcDate =
              typeof rawValue === "number"
                ? DateTime.fromSeconds(rawValue, { zone: "utc" })
                : DateTime.fromISO(rawValue || "", { zone: "utc" });

            const requestedRaw = params?.row?.requested_created_at;
            const requested =
              typeof requestedRaw === "number"
                ? DateTime.fromSeconds(requestedRaw, { zone: "utc" })
                : DateTime.fromISO(requestedRaw || "", { zone: "utc" });
            if (!requested.isValid) {
              return <span>N/A</span>;
            }

            const now = DateTime.fromMillis(currentTime, { zone: "utc" });
            const totalSeconds = Math.max(
              0,
              Math.floor(now.diff(requested, "seconds").seconds),
            );
            const hours = Math.floor(totalSeconds / 3600);
            const minutes = Math.floor((totalSeconds % 3600) / 60);
            const seconds = totalSeconds % 60;

            const signer = params?.row?.signer || "N/A";

            return (
              <>
                <Box sx={{ display: "flex", width: "100%", pr: 2 }}>
                  <Box
                    sx={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      pb: 0.2,
                      pl: 1,
                      minWidth: "180px",
                      width: "100%",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "0.90rem",
                        color: "rgba(0, 0, 0, 0.8)",
                        fontWeight: 500,
                      }}
                    >
                      {!utcDate.isValid
                        ? "N/A"
                        : utcDate
                            .setZone(currentUser?.timezone)
                            .toFormat("MMMM dd, yyyy hh:mm a")}
                    </Typography>

                    <Typography
                      variant="body1"
                      sx={{
                        fontSize: "0.85rem",
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                        color: "rgba(0,0,0,0.62)",
                      }}
                    >
                      {params?.row?.project_address}
                    </Typography>

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                          alignItems: "center",
                          mt: 0.5,
                        }}
                      >
                        <Chip
                          label={
                            signer.charAt(0).toUpperCase() + signer.slice(1)
                          }
                          color={signer === "owner" ? "primary" : "secondary"}
                          size="small"
                        />
                        <Chip
                          label={`${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${seconds
                            .toString()
                            .padStart(2, "0")}`}
                          color="warning"
                          size="small"
                        />
                      </Box>

                      {extraSmall &&
                        expiredRequestActions(
                          params?.row,
                          handleExpiredExpeditedRequestClick,
                          openExpiredExpeditedAcceptConfirm,
                        )}
                    </Box>
                  </Box>
                  {!extraSmall &&
                    expiredRequestActions(
                      params?.row,
                      handleExpiredExpeditedRequestClick,
                      openExpiredExpeditedAcceptConfirm,
                    )}
                </Box>
              </>
            );
          },
        },
      ]
    : [
        {
          field: "requested_created_at",
          headerName: "Requested At",
          flex: 1,
          renderCell: (params) => {
            const rawValue = params.value;
            const utcDate =
              typeof rawValue === "number"
                ? DateTime.fromSeconds(rawValue, { zone: "utc" })
                : DateTime.fromISO(rawValue || "", { zone: "utc" });

            if (!utcDate.isValid) {
              return <span>N/A</span>;
            }

            const usDate = utcDate.setZone(currentUser?.timezone);
            const formattedDate = usDate.toFormat("MMMM dd, yyyy hh:mm a");
            return <span>{formattedDate}</span>;
          },
        },
        {
          field: "signer",
          headerName: "Signer",
          flex: 0.5,
          renderCell: (params) => {
            const signer = params.value || "N/A";
            return (
              <Chip
                label={signer.charAt(0).toUpperCase() + signer.slice(1)}
                color={signer === "owner" ? "primary" : "secondary"}
                size="small"
              />
            );
          },
        },
        {
          field: "project_address",
          headerName: "Project Address",
          flex: 1,
        },
        {
          field: "elapsed",
          headerName: "Elapsed",
          flex: 0.6,
          renderCell: (params) => {
            const requestedRaw = params.row?.requested_created_at;
            const requested =
              typeof requestedRaw === "number"
                ? DateTime.fromSeconds(requestedRaw, { zone: "utc" })
                : DateTime.fromISO(requestedRaw || "", { zone: "utc" });
            if (!requested.isValid) {
              return <span>N/A</span>;
            }

            const now = DateTime.fromMillis(currentTime, { zone: "utc" });
            const totalSeconds = Math.max(
              0,
              Math.floor(now.diff(requested, "seconds").seconds),
            );
            const hours = Math.floor(totalSeconds / 3600);
            const minutes = Math.floor((totalSeconds % 3600) / 60);
            const seconds = totalSeconds % 60;

            return (
              <Chip
                label={`${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${seconds
                  .toString()
                  .padStart(2, "0")}`}
                color="warning"
                size="small"
              />
            );
          },
        },
        {
          field: "actions",
          type: "actions",
          headerName: "Actions",
          width: 160,
          getActions: (params) => [
            <GridActionsCellItem
              icon={
                <Tooltip title="View Details">
                  <Visibility />
                </Tooltip>
              }
              label="View"
              onClick={() => handleExpiredExpeditedRequestClick(params.row)}
            />,
            <GridActionsCellItem
              icon={
                <Tooltip title="Accept Request">
                  <Schedule />
                </Tooltip>
              }
              label="Accept"
              onClick={() => openExpiredExpeditedAcceptConfirm(params.row)}
            />,
          ],
        },
      ];
